import axios from "axios";
import { setAlert } from "./alert";
import { LOGIN_SUCCESS, LOGIN_FAIL, LOGOUT, CLEAR_USER, MFA_QR_CODE, MFA_ENABLE } from "./types";
import setAuthToken from "../utils/setAuthToken";
import { withExpiresFromPayload } from "../utils/sessionAuth";
import customConfig from "../config";
import env from "../env";
// Status Logs column prefs in localStorage — cleared on logout (see deviceStatusColumnUtils.js).
import { clearDeviceStatusColumnPreferences } from "../components/device/DeviceStatus/deviceStatusColumnUtils";

const sortCategories = (list = []) =>
  Array.from(
    new Set(
      (Array.isArray(list) ? list : [])
        .filter(Boolean)
        .map((item) => String(item))
    )
  ).sort((a, b) => a.localeCompare(b));

/* Login: POST body uses captchaToken + captchaProvider when CAPTCHA is required; tenant comes from axios default X-Tenant-ID (setTenantId). Alerts: 401 uses API message, 403 account locked. */
export const login =
  (dispatch) => async (userName, password, clientId, scope, captchaToken, captchaProvider = "google") => {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const body = { userName, password, clientId };
    if (captchaToken) {
      body.captchaToken = captchaToken;
      body.captchaProvider = captchaProvider;
    }
    try {
      let umURL = env.GET_USERMANAGEMENT_API_URL()
      let authURL =  `${umURL}/simplifi/v1/userManagement/login`

      const res = await axios.post(
        authURL,
        body,
        config
      );

      // MFA hotfix: when disabled, ignore the MFA challenge so the validation
      // screen never appears and validateMFA is never called. Login proceeds
      // with the accessToken from this response (handled in Login.loginForm).
      if (!customConfig.mfaDisabled && res.data.mfaToken) {
        dispatch({
          type: MFA_ENABLE,
          payload: {
            ...res.data
          }
        })
      }
      return res;
    } catch (err) {
      console.log("This is error : ", err);
      if (!err.response) {
        dispatch(setAlert(customConfig.serverDownMsg, "danger"));
      } else if (err.response.status === 403) {
        dispatch(setAlert(err?.response?.data?.message || "Account is locked. Please contact support.", "danger"));
      } else if (err.response.status === 401) {
        dispatch(setAlert(err?.response?.data?.message || customConfig.authMsg, "danger"));
      } else if (err.response.status === 429) {
        dispatch(setAlert(err?.response?.data?.message || customConfig.authMsg, "danger"));
      } else if ([400, 412, 422].includes(err.response.status)) {
        dispatch(setAlert(err?.response?.data?.message || customConfig.authMsg, "danger"));
      } else if (err.response.status === 500) {
        //added for invalid captcha
        dispatch(setAlert(err?.response?.data?.message || customConfig.authMsg, "danger"));
      } else {
        dispatch(setAlert(customConfig.authMsg, "danger"));
      }
      dispatch({
        type: LOGIN_FAIL,
      });
      throw err;
    }
  };

//LOGOUT
export const logout = () => async (dispatch) => {
  let umURL = env.GET_USERMANAGEMENT_API_URL()
  const clientId = env.GET_CLIENT_ID()

  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  const body = { clientId };
  dispatch({ type: CLEAR_USER });
  dispatch({ type: LOGOUT });
  localStorage.removeItem("token");
  localStorage.removeItem("userInfo");
  localStorage.removeItem("userRoles");
  localStorage.removeItem("userLevel");
  localStorage.removeItem("loggedInUserName");
  localStorage.removeItem("tenantInfo")
  localStorage.removeItem("authInfo")
  clearDeviceStatusColumnPreferences();
  localStorage.removeItem("feedback.visibleColumns");

  try {
    const res =  await axios.post(
      `${umURL}/simplifi/v1/userManagement/logout`,
      body,
      config
    )
    window.location.reload();
  } catch (err) { }
  setAuthToken();
  if (window && window.location && window.location.reload) {
    window.location.reload();
  } else if (document && document.location && document.location.reload) {
    document.location.reload();
  }
};

export const addMfaDevice = (accessToken) => async (dispatch) => {
  try {
    let umURL = env.GET_USERMANAGEMENT_API_URL();
    const config = {
      headers: {
          "Content-Type": "application/json",
      },
    };
    const res = await axios.post(
      `${umURL}/simplifi/v2/userManagement/mfa/addMFADevice`,
      {},
      config
    );
    dispatch({
      type: MFA_QR_CODE,
      payload: res.data,
    });
    return res.data;
  } catch (err) {
    console.error("MFA Device Error", err);
      if (!err.response) {
        dispatch(setAlert(customConfig.serverDownMsg, "danger"));
      } else if (err.response.status === 429) {
        dispatch(setAlert(err?.response?.data?.message || customConfig.addMfaDeviceError, "danger"));
      } else if (err.response.status === 500) {
        //added for invalid captcha
        dispatch(setAlert(err?.response?.data?.message || customConfig.addMfaDeviceError, "danger"));
      } else {
        dispatch(setAlert(customConfig.addMfaDeviceError, "danger"));
      }
  }
};

export const syncMFA = (codes) => async (dispatch) => {
    try {
        let umURL = env.GET_USERMANAGEMENT_API_URL();
        let syncUrl = `${umURL}/simplifi/v2/userManagement/mfa/resyncMFADevice`;

        const config = {
            headers: {
                "Content-Type": "application/json",
            },
        };

        const body = {
            mfaCode1: codes.code1,
            mfaCode2: codes.code2, 
        };

        const res = await axios.post(syncUrl, body, config);
        
        if(res.status === 200) {
          dispatch(setAlert(customConfig.mfaSyncSuccess, "success"))
          setTimeout(() => {
            window.location.reload(); // Adjust the path as needed
          }, 3000);
        }

    } catch (err) {
      if (!err.response) {
        dispatch(setAlert(customConfig.serverDownMsg, "danger"));
      } else if (err.response.status === 400) {
        dispatch(setAlert(customConfig.mfaInvalid, "danger"));
      } else if (err.response.status === 500) {
        //added for invalid captcha
        dispatch(setAlert(err?.response?.data?.message || customConfig.mfaSyncError, "danger"));
      } else {
        dispatch(setAlert(customConfig.mfaSyncError, "danger"));
      }
    }
};

export const mfaValidate = (otp, mfaToken) => async (dispatch) => {
  try {
    let umURL = env.GET_USERMANAGEMENT_API_URL();
    let validateUrl = `${umURL}/simplifi/v2/userManagement/mfa/validateMFA`;
    const body = {
      mfaToken: mfaToken,
      otp: otp,

    };
    const config = {
      headers: {
          "Content-Type": "application/json",
      },
  };
    const res = await axios.post(
      validateUrl,
      body,
      config
    );

    return res

  } catch (err) {
    console.log("This is error : ", err);
      if (!err.response) {
        dispatch(setAlert(customConfig.serverDownMsg, "danger"));
      } else if (err.response.status === 429) {
        dispatch(setAlert(err?.response?.data?.message || customConfig.mfaInvalid, "danger"));
      } else if (err.response.status === 500) {
        //added for invalid captcha
        dispatch(setAlert(err?.response?.data?.message || customConfig.mfaInvalid, "danger"));
      } else {
        dispatch(setAlert(customConfig.mfaInvalid, "danger"));
      }
      dispatch({
        type: LOGIN_FAIL,
      });
  }
};

export const disableMFA = () => async (dispatch) => {
  try {
    let umURL = env.GET_USERMANAGEMENT_API_URL();
    let disableUrl = `${umURL}/simplifi/v2/userManagement/mfa/disableMFA`;

    const config = {
        headers: {
            "Content-Type": "application/json",
        },
    };

    const res = await axios.post(disableUrl, {}, config);

    if(res.status === 200) {
      dispatch(setAlert(customConfig.mfaDisableSuccess, "success"))
      setTimeout(() => {
        logout(); 
      }, 2000);
    }

  } catch (err) {
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else if (err.response.status === 400) {
      dispatch(setAlert(customConfig.mfaInvalid, "danger"));
    } else if (err.response.status === 500) {
      //added for invalid captcha
      dispatch(setAlert(err?.response?.data?.message || customConfig.mfaDisableError, "danger"));
    } else {
      dispatch(setAlert(customConfig.mfaDisableError, "danger"));
    }
  }

};

export const validateUserRole = (res) => async (dispatch) => {
  try {
    // Check role of logged in user
    const umURL = env.GET_USERMANAGEMENT_API_URL();
    const configVal = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${res.data.accessToken}`,
      },
    };
    const resVal = await axios.get(
      `${umURL}/simplifi/v1/userManagement/tokenDetails`,
      configVal
    );

    // Check for valid roles
    let userLevel = 0;
    let roleList = resVal.data.roles;
    let loggedInUserRoles = ""

    // localStorage.setItem("loggedInUserName", resVal.data.username);
    if (roleList.indexOf(customConfig.roleCustomerSupportL4) > -1) {
      userLevel = 5;
    } else if (
      roleList.indexOf(customConfig.roleCustomerSupportL3Special) > -1
    ) {
      userLevel = 4;
    } else if (roleList.indexOf(customConfig.roleCustomerSupportL3) > -1) {
      userLevel = 3;
    } else if (roleList.indexOf(customConfig.roleCustomerSupportL2) > -1) {
      userLevel = 2;
    } else if (roleList.indexOf(customConfig.roleCustomerSupportL1) > -1) {
      userLevel = 1;
    } else if (roleList.indexOf(customConfig.roleFactoryAdmin) > -1) {
      loggedInUserRoles = customConfig.roleFactoryAdmin
    }


    let allowCompanyBrand = [];
    const uniqueBrands = new Set();
    let allowedDeviceCategories = []
    const uniqueCategories = new Set();
    let allowedCountries = [];
    let allowedCountryCodes = [];

    resVal.data.permissions.forEach((element) => {
      if (element.country === "BGD") {
        allowedCountries.push("BD".toLowerCase());
      }
      else if (element.country.substring(0, 2) === "SL") {
        allowedCountries.push("LK".toLowerCase()); //LK as country code for srilanka
      }
      else {
        allowedCountries.push(element.country.toLowerCase());
      }

      if(element?.country && element?.country.toLowerCase() !== "idp"){
        // Using Set to ensure uniqueness and improve performance
        element?.categories.forEach(category => {
          uniqueCategories.add(category);
        });
      }

      if(element?.brand && element?.brand.toLowerCase() !== "sys"){
        uniqueBrands.add(element?.brand);
      }

      if (allowedCountryCodes.indexOf(element.country)===-1) {
        allowedCountryCodes.push(element.country)
      }
    });

    allowCompanyBrand = [...uniqueBrands];

    // For converting from object to array
    allowedDeviceCategories = [...uniqueCategories];

    //for removing duplicate countries in list
    allowedCountries = allowedCountries.filter(
      (value, index, self) => self.indexOf(value) === index
    );

    // Modified the login.. Now Factory Admin Users can also log in to webapp
    if (userLevel > 0 || [customConfig.roleFactoryAdmin].includes(loggedInUserRoles)) {
      const loginPayload = withExpiresFromPayload({
        ...res.data,
        ...resVal.data,
        allowedCountries,
        userRoles: roleList,
        userLevel: userLevel,
        allowedDeviceCategories: sortCategories(allowedDeviceCategories),
        allowCompanyBrand,
        allowedCountryCodes,
      });
      dispatch({
        type: LOGIN_SUCCESS,
        payload: loginPayload,
      });
      localStorage.setItem("authInfo", JSON.stringify(loginPayload));
      setAuthToken(res.data.accessToken);
      return res;
    } else {
      dispatch(setAlert(customConfig.customerSupportRoleErrorMsg, "danger"));
      dispatch({
        type: LOGIN_FAIL,
      });
    }
  } catch (err) {
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else if (err.response.status === 429) {
      dispatch(setAlert(err.response.data.message, "danger"));
    } else if (err.response.status === 500) {
      dispatch(setAlert(err.response.data.message, "danger"));
    } else {
      dispatch(setAlert(customConfig.authMsg, "danger"));
    }
    dispatch({
      type: LOGIN_FAIL,
    });
    return null;
  }
};
